import { Link } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type { ProductionFormRow } from '@/features/manufacturing/hooks';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/formatters';
import { printDocument } from '@/lib/printDocument';

interface ProductionFormSheetProps {
  form: ProductionFormRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function printProductionForm(form: ProductionFormRow) {
  const opened = printDocument({
    title: 'Production form',
    number: form.production_number,
    status: form.bom_versions ? `BOM v${form.bom_versions.version}` : 'No BOM',
    subtitle: `${form.items?.sku ?? ''} · ${form.items?.name ?? 'Unknown item'}`,
    sections: [
      {
        title: 'Production details',
        fields: [
          { label: 'Produced', value: formatDate(form.produced_at) },
          { label: 'Submitted', value: formatDateTime(form.created_at) },
          { label: 'Built by', value: form.builder?.full_name || form.builder?.email },
          { label: 'Workstation', value: form.workstations?.name },
          {
            label: 'Good quantity',
            value: `${form.quantity.toLocaleString()} ${form.items?.base_uom ?? ''}`.trim(),
          },
          { label: 'Scrap quantity', value: form.scrap_quantity.toLocaleString() },
          {
            label: 'Put away to',
            value: form.locations ? `${form.locations.name} (${form.locations.code})` : null,
          },
          { label: 'Lot / batch', value: form.lots?.lot_number },
          { label: 'Build cost', value: formatCurrency(form.component_cost_cents) },
          { label: 'Cost per good unit', value: formatCurrency(form.unit_cost_cents) },
        ],
      },
      {
        title: 'Components consumed',
        table: {
          columns: [
            { label: 'SKU' },
            { label: 'Component' },
            { label: 'Lot' },
            { label: 'From' },
            { label: 'Used', align: 'right' },
            { label: 'Cost', align: 'right' },
          ],
          rows: form.production_form_components.map((component) => [
            component.items?.sku,
            component.items?.name,
            component.lots?.lot_number,
            component.locations?.code,
            component.quantity.toLocaleString(),
            component.unit_cost_cents == null
              ? null
              : formatCurrency(component.unit_cost_cents * component.quantity),
          ]),
        },
      },
      ...(form.notes ? [{ title: 'Notes', text: form.notes }] : []),
    ],
  });
  if (!opened) toast.error('Allow pop-ups to print or save this production form as a PDF.');
}

/** Read-only view of one submitted production form and what it consumed. */
export function ProductionFormSheet({ form, open, onOpenChange }: ProductionFormSheetProps) {
  if (!form) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent />
      </Sheet>
    );
  }

  const uom = form.items?.base_uom ?? '';
  const components = form.production_form_components;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <div className="flex flex-wrap items-center gap-2">
            {form.bom_versions ? (
              <Badge variant="secondary">BOM v{form.bom_versions.version}</Badge>
            ) : (
              <Badge variant="muted">No BOM</Badge>
            )}
            {form.scrap_quantity > 0 && (
              <Badge variant="warning-soft">{form.scrap_quantity.toLocaleString()} scrapped</Badge>
            )}
          </div>
          <SheetTitle>{form.production_number}</SheetTitle>
          <SheetDescription>
            Produced {formatDate(form.produced_at)} · submitted {formatDateTime(form.created_at)}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-6">
          <div className="rounded-md border border-border bg-secondary/30 px-4 py-3">
            {form.items ? (
              <Link
                to={`/catalog/items/${form.item_id}?tab=manufacturing`}
                className="font-mono text-xs text-muted-foreground hover:underline"
              >
                {form.items.sku}
              </Link>
            ) : (
              <div className="font-mono text-xs text-muted-foreground">—</div>
            )}
            <div className="font-medium">{form.items?.name ?? 'Unknown item'}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
              {form.quantity.toLocaleString()}
              <span className="ml-1 text-sm font-normal text-muted-foreground">{uom} good</span>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Put away to">
              {form.locations ? `${form.locations.name} (${form.locations.code})` : '—'}
            </Field>
            <Field label="Lot / batch">
              <span className="font-mono text-xs">{form.lots?.lot_number ?? '—'}</span>
            </Field>
            <Field label="Workstation">{form.workstations?.name ?? '—'}</Field>
            <Field label="Built by">{form.builder?.full_name || form.builder?.email || '—'}</Field>
            <Field label="Build cost">
              <span className="tabular-nums">{formatCurrency(form.component_cost_cents)}</span>
            </Field>
            <Field label="Cost per good unit">
              <span className="tabular-nums">{formatCurrency(form.unit_cost_cents)}</span>
            </Field>
          </dl>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">Components consumed</h3>
            {components.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No components were recorded on this form.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Component</th>
                      <th className="px-2 py-2 text-right font-medium">Used</th>
                      <th className="px-2 py-2 text-left font-medium">From</th>
                      <th className="px-3 py-2 text-right font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((c) => {
                      const expected =
                        c.expected_quantity == null ? null : Number(c.expected_quantity);
                      const variance = expected != null && Math.ceil(expected) !== c.quantity;
                      return (
                        <tr key={c.id} className="border-t border-border align-top">
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs text-muted-foreground">
                              {c.items?.sku ?? '—'}
                            </div>
                            <div>{c.items?.name ?? 'Unknown item'}</div>
                            {c.lots?.lot_number && (
                              <div className="font-mono text-[11px] text-muted-foreground">
                                Lot {c.lots.lot_number}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            {c.quantity.toLocaleString()}
                            {variance && (
                              <div className="text-[11px] text-muted-foreground">
                                BOM {Number(expected.toFixed(4)).toLocaleString()}
                              </div>
                            )}
                            {expected == null && (
                              <div className="text-[11px] text-muted-foreground">Added</div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-muted-foreground">
                            {c.locations?.code ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {c.unit_cost_cents == null
                              ? '—'
                              : formatCurrency(c.unit_cost_cents * c.quantity)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {form.notes && (
            <section className="space-y-1">
              <h3 className="text-sm font-medium">Notes</h3>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{form.notes}</p>
            </section>
          )}
        </SheetBody>
        <SheetFooter>
          <Button type="button" variant="outline" onClick={() => printProductionForm(form)}>
            <Printer className="h-4 w-4" />
            Print / PDF
          </Button>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
